import { useCallback, useEffect, useState } from 'react'
import { contactsApi, contactInteractionsApi, Contact, ContactInteraction } from '../services/api'

interface NetworkState {
  clientId: string | null
  contacts: Contact[]
  interactions: ContactInteraction[]
  loading: boolean
  error: boolean
}

const EMPTY: NetworkState = { clientId: null, contacts: [], interactions: [], loading: false, error: false }

/**
 * One client's networking contacts and their interactions, as the job
 * applications pages read them.
 *
 * A contacts failure is reported rather than swallowed (MWA-358): it used to
 * become an empty list, which the job dialog then presented as "nobody in the
 * client's network" — a false answer to the question the section exists to
 * answer. Interactions stay best-effort, since they only add history to rows
 * that are already there.
 *
 * Results are tagged with the client they belong to and only returned for that
 * client, so switching clients can never show one client's network under
 * another's opportunity while the new request is in flight.
 */
export function useClientNetwork(clientId: string | null) {
  const [state, setState] = useState<NetworkState>(EMPTY)
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    if (!clientId) {
      setState(EMPTY)
      return
    }
    let cancelled = false
    setState((prev) =>
      prev.clientId === clientId
        ? { ...prev, loading: true, error: false }
        : { ...EMPTY, clientId, loading: true }
    )
    contactsApi
      .list(clientId)
      .then((res) => {
        if (cancelled) return
        setState((prev) => ({ ...prev, contacts: Array.isArray(res) ? res : [], loading: false, error: false }))
      })
      .catch((err) => {
        if (cancelled) return
        console.error(`Failed to load contacts for client ${clientId}:`, err)
        setState((prev) => ({ ...prev, contacts: [], loading: false, error: true }))
      })
    contactInteractionsApi
      .list({ clientId })
      .then((res) => {
        if (!cancelled) setState((prev) => ({ ...prev, interactions: Array.isArray(res) ? res : [] }))
      })
      .catch(() => {
        if (!cancelled) setState((prev) => ({ ...prev, interactions: [] }))
      })
    return () => {
      cancelled = true
    }
  }, [clientId, attempt])

  const retry = useCallback(() => setAttempt((n) => n + 1), [])

  const replaceContact = useCallback((updated: Contact) => {
    setState((prev) => ({
      ...prev,
      contacts: prev.contacts.map((c) => (c.id === updated.id ? updated : c)),
    }))
  }, [])

  const current = state.clientId === clientId ? state : EMPTY
  return {
    contacts: current.contacts,
    interactions: current.interactions,
    loading: clientId !== null && (current.loading || state.clientId !== clientId),
    error: current.error,
    retry,
    replaceContact,
  }
}
